<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><app-button @click.native="onShowModal(undefined)">Add group</app-button></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #count="{ index }">{{ index }}</template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #sort="{ row }">{{ row.sort || '-' }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="edit-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('edit-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ !item_to_update ? 'Add' : 'Update' }} group</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="name" required>
              <app-input
                v-model="form.name"
                placeholder="Name"
                id="name"
                name="name"
                :error="$v.form.name.$error"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Sort" label-for="sort">
              <app-input
                v-model="form.sort"
                placeholder="Sort"
                id="sort"
                name="sort"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit" :disabled="$v.form.$error">{{ !item_to_update ? 'Add' : 'Save' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete group
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this group?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="deleteItem">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getFinanceTransactionsItemGroupsList,
  postFinanceTransactionsItemGroup,
  patchFinanceTransactionsItemGroup,
  deleteFinanceTransactionsItemGroup
} from '@/http'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FinanceDictionariesTransactionItemGroups',
  data() {
    return {
      page_loaded: false,
      item_to_delete: null,
      item_to_update: null,
      table_preloader: false,
      form: {},
      table: {
        items: [],
        columns: [
          'count',
          'name',
          'sort',
          'controls'
        ],
        options: {
          headings: {
            'count': '#',
            'name': 'Name',
            'sort': 'Sort',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  validations: {
    form: {
      name: { required },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getFinanceTransactionsItemGroupsList({ page: this.pagination.page, page_size: 20 })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.page_loaded = true
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      const data = {
        name: this.form.name,
        sort: this.form.sort,
      }
      if (this.item_to_update) {
        patchFinanceTransactionsItemGroup(this.item_to_update, data)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Group updated',
            })
            this.$modal.hide('edit-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      } else {
        postFinanceTransactionsItemGroup(this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Group created',
            })
            this.$modal.hide('edit-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      }
    },
    onDeleteItem() {
      this.$refs.deleteItem.preload = true
      deleteFinanceTransactionsItemGroup(this.item_to_delete)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Group deleted',
          })
          this.$modal.hide('delete-modal')
        })
        .catch(() => {
          this.$refs.deleteItem.preload = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal(data = {}) {
      this.item_to_update = data.id || null
      this.form = data
      this.$v.form.$reset()
      this.$modal.show('edit-modal')
    },
    onShowDeleteModal(item_id) {
      this.item_to_delete = item_id
      this.$modal.show('delete-modal')
    },
  }
}
</script>
